import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, isFirebaseConfigured } from "./services/firebase";
import {
  saveAddress as saveAddressRemote,
  deleteAddress as deleteAddressRemote,
  createSchedule,
  createOrder,
  updateSchedule,
  subscribeToOrders,
  subscribeToUserAddresses,
  subscribeToUserProfile,
  subscribeToUserNotifications,
  updateOrdersForSchedule,
  updateOrderAndSchedule as updateOrderAndScheduleRemote,
  updateConcreteOrder as updateConcreteOrderRemote,
  updateOrder as updateOrderRemote,
  updateOrderStatus as updateOrderStatusRemote,
} from "./services/firestore";
import { initialAddresses, sampleDeliveries, makeNotifications } from "./data";
import { logoutUser, updateUserProfile } from "./services/auth";
import { isOrderModifiable, nextDeliveryDate } from "./utils/admin";

const AppContext = createContext(null);
const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(() =>
    isFirebaseConfigured() ? null : read("freshloop_user", null),
  );
  const [authLoading, setAuthLoading] = useState(isFirebaseConfigured());
  const [addresses, setAddresses] = useState(() =>
    isFirebaseConfigured() ? [] : read("freshloop_addresses", initialAddresses),
  );
  const [deliveries, setDeliveries] = useState(() =>
    isFirebaseConfigured() ? [] : read("freshloop_deliveries", sampleDeliveries),
  );
  const [notifications, setNotifications] = useState(() =>
    isFirebaseConfigured() ? [] : read("freshloop_notifications", makeNotifications(sampleDeliveries)),
  );
  const [draft, setDraft] = useState(() => read("freshloop_draft", null));

  useEffect(() => {
    if (!isFirebaseConfigured() || !auth) {
      setAuthLoading(false);
      return undefined;
    }

    return onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setAddresses([]);
        setDeliveries([]);
        setNotifications([]);
        setAuthLoading(false);
        return;
      }

      setUser({
        uid: firebaseUser.uid,
        name: firebaseUser.displayName || "FreshLoop Customer",
        email: firebaseUser.email || "",
        mobile: firebaseUser.phoneNumber || "",
      });
      setAuthLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isFirebaseConfigured() || !user?.uid || user.uid === "local-user") return undefined;

    const stopProfile = subscribeToUserProfile(user.uid, (profile) => {
      if (!profile) return;
      setUser((current) => ({
        ...current,
        ...profile,
        uid: user.uid,
        name: profile.name || current?.name || "FreshLoop Customer",
        email: profile.email || current?.email || "",
        mobile: profile.mobile || current?.mobile || "",
      }));
    });

    const stopAddresses = subscribeToUserAddresses(user.uid, setAddresses);
    const stopOrders = subscribeToOrders(user.uid, (list) => {
      setDeliveries(list);
      setNotifications(makeNotifications(list));
    });
    const stopNotifications = subscribeToUserNotifications(user.uid, setNotifications);

    return () => {
      stopProfile?.();
      stopAddresses?.();
      stopOrders?.();
      stopNotifications?.();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!isFirebaseConfigured()) localStorage.setItem("freshloop_user", JSON.stringify(user));
  }, [user]);
  useEffect(() => {
    if (!isFirebaseConfigured()) localStorage.setItem("freshloop_addresses", JSON.stringify(addresses));
  }, [addresses]);
  useEffect(() => {
    if (!isFirebaseConfigured()) localStorage.setItem("freshloop_deliveries", JSON.stringify(deliveries));
  }, [deliveries]);
  useEffect(() => {
    if (!isFirebaseConfigured()) localStorage.setItem("freshloop_notifications", JSON.stringify(notifications));
  }, [notifications]);
  useEffect(() => {
    if (draft) localStorage.setItem("freshloop_draft", JSON.stringify(draft));
    else localStorage.removeItem("freshloop_draft");
  }, [draft]);

  const value = useMemo(() => ({
    user,
    setUser,
    authLoading,
    addresses,
    deliveries,
    setDeliveries,
    notifications,
    draft,
    setDraft,

    updateUser: async (data) => {
      const updated = await updateUserProfile(data);
      setUser((current) => ({ ...current, ...updated }));
    },

    saveSchedule: async (data) => createSchedule(user?.uid, data),
    saveOrder: async (data) => createOrder(user?.uid, data),
    updateSchedule: async (id, data) => updateSchedule(id, data),
    updateOrdersForSchedule: async (scheduleId, status, fromDate, toDate) =>
      updateOrdersForSchedule(user?.uid, scheduleId, status, fromDate, toDate),

    updateOrderAndSchedule: async (scheduleId, data) => {
      if (!isFirebaseConfigured()) {
        let updatedOrders = 0;
        let lockedOrders = 0;
        let oneTimeAssigned = false;
        const usedDates = new Set();
        setDeliveries((previous) =>
          previous.map((item) => {
            if (item.scheduleId !== scheduleId || ["Delivered", "Cancelled", "Skipped"].includes(item.status)) return item;
            if (!isOrderModifiable(item)) {
              lockedOrders += 1;
              return item;
            }
            if (data.frequency === "One Time" && oneTimeAssigned) {
              return { ...item, status: "Cancelled" };
            }
            const base = item.deliveryDate || data.startDate;
            let deliveryDate = item.deliveryDate;
            const recurrenceChanged =
              data.frequency !== item.frequency ||
              JSON.stringify(data.days || []) !== JSON.stringify(item.days || []);
            if (recurrenceChanged) {
              for (let i = 0; i < 370; i += 1) {
                const candidate = nextDeliveryDate(
                  { ...data, status: "active" },
                  new Date(`${base}T00:00:00`),
                );
                if (!candidate || usedDates.has(candidate)) break;
                deliveryDate = candidate;
                break;
              }
            }
            if (deliveryDate) usedDates.add(deliveryDate);
            if (data.frequency === "One Time") oneTimeAssigned = true;
            updatedOrders += 1;
            return {
              ...item,
              deliveryDate,
              timeSlot: data.deliveryTimeSlot || item.timeSlot,
              deliveryTimeSlot: data.deliveryTimeSlot || item.deliveryTimeSlot || item.timeSlot,
              addressId: data.addressId ?? item.addressId,
              quantity: Number(data.quantity || item.quantity || 1),
              frequency: data.frequency || item.frequency,
              recurring: data.frequency !== "One Time",
              days: Array.isArray(data.days) ? data.days : item.days || [],
            };
          }),
        );
        return { updatedOrders, lockedOrders, skippedOrders: 0 };
      }
      return updateOrderAndScheduleRemote(user?.uid, scheduleId, data);
    },

    updateConcreteOrder: async (id, data) => {
      if (!isFirebaseConfigured()) {
        const current = deliveries.find((item) => item.id === id);
        if (!current) throw new Error("This delivery no longer exists. Refresh and try again.");
        if (["Delivered", "Cancelled", "Skipped"].includes(current.status)) {
          throw new Error("Completed, cancelled, or skipped deliveries cannot be modified.");
        }
        if (!isOrderModifiable(current)) {
          throw new Error("This delivery is inside the 24-hour modification window and cannot be changed.");
        }
        const updated = { ...current, ...data };
        if (!isOrderModifiable(updated)) {
          throw new Error("The requested new delivery date/time would fall inside the 24-hour modification window.");
        }
        setDeliveries((previous) => previous.map((item) => item.id === id ? updated : item));
        return updated;
      }
      const updated = await updateConcreteOrderRemote(user?.uid, id, data);
      setDeliveries((previous) => previous.map((item) => item.id === id ? { ...item, ...updated } : item));
      return updated;
    },

    updateOrder: async (id, data) => {
      await updateOrderRemote(id, data);
      setDeliveries((previous) =>
        previous.map((item) => (item.id === id ? { ...item, ...data } : item)),
      );
    },

    updateOrderStatus: async (id, status) => {
      await updateOrderStatusRemote(id, status);
      setDeliveries((previous) => previous.map((item) => item.id === id ? { ...item, status } : item));
    },

    saveAddress: async (address) => {
      if (!user?.uid) throw new Error("Please register or login before saving an address.");
      const saved = await saveAddressRemote(user.uid, address);
      if (!isFirebaseConfigured()) {
        setAddresses((previous) => {
          const exists = previous.some((item) => item.id === saved.id);
          return exists ? previous.map((item) => item.id === saved.id ? saved : item) : [...previous, saved];
        });
      }
      return saved;
    },

    deleteAddress: async (id) => {
      await deleteAddressRemote(id);
      setAddresses((previous) => previous.filter((item) => item.id !== id));
    },

    logout: async () => {
      await logoutUser();
      localStorage.removeItem("freshloop_user");
      setUser(null);
      if (isFirebaseConfigured()) {
        setAddresses([]);
        setDeliveries([]);
        setNotifications([]);
      }
    },
  }), [user, authLoading, addresses, deliveries, notifications, draft]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used inside AppProvider");
  return context;
}
